import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb, { generateBreadcrumbLD } from '@/components/feature/Breadcrumb';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = useMemo(
    () => categories.find(c => c.slug === slug),
    [slug],
  );
  const comics = useMemo(
    () => getComicsByCategory(slug || 'all'),
    [slug],
  );
  const categoryName = currentCategory?.name || '全部';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className={`${currentCategory?.icon || 'ri-apps-line'} text-xl`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                  {categoryName}
                </h1>
              </div>
              <p className="text-sm text-foreground-500 ml-[52px]">
                共找到 <strong>{comics.length}</strong> 部{categories.find(c => c.slug === slug) ? '' : '韩漫'}漫画作品
              </p>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap mb-8">
              {categories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {comics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {comics.map(comic => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无漫画</p>
              </div>
            )}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify([
                {
                  '@context': 'https://schema.org',
                  '@type': 'CollectionPage',
                  name: `${categoryName} - 韩漫漫画`,
                  description: `韩漫漫画${categoryName}分类，共${comics.length}部精选韩漫作品，正版资源在线阅读`,
                  url: `https://www.manhuakr.com/category/${slug}`,
                  isPartOf: {
                    '@type': 'WebSite',
                    name: '韩漫漫画',
                    url: 'https://www.manhuakr.com/',
                  },
                },
                generateBreadcrumbLD(breadcrumbItems),
              ]),
            }}
          />
        </main>
        <Footer />
      </div>
    </>
  );
}